import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import TableExportButton from "@/components/TableExportButton";
import {
  Landmark, FileText, IndianRupee, Plus, Save, X, Search, Printer, CheckCircle2,
  Eye, Pencil, Edit, Trash2, Building2,
} from "lucide-react";
import AssetRegisterTab from "./AssetRegisterTab";
import RunningBalancePanel from "@/components/RunningBalancePanel";

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
const monoCls = `${inputCls} font-mono text-right`;

export default function Account() {
  const [tab, setTab] = useState("bill"); // 'bill' | 'pay' | 'assets'
  return (
    <div className="space-y-6" data-testid="account-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">Module · Accounts</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
          <Landmark size={22} /> Account
        </h1>
        <p className="text-sm text-slate-500 mt-1">Book supplier bills, pay them via voucher slips, and track fixed assets.</p>
      </div>

      <div className="flex items-center gap-3 border-b border-slate-200">
        <TabBtn active={tab === "bill"} onClick={() => setTab("bill")} testid="tab-bill" icon={FileText} label="Bill Booking" />
        <TabBtn active={tab === "pay"} onClick={() => setTab("pay")} testid="tab-pay" icon={IndianRupee} label="Payment / Transaction" />
        <TabBtn active={tab === "assets"} onClick={() => setTab("assets")} testid="tab-assets" icon={Building2} label="Asset Register" />
      </div>

      {tab === "bill" && <BillBookingTab />}
      {tab === "pay" && <PaymentTab />}
      {tab === "assets" && <AssetRegisterTab />}
    </div>
  );
}

const TabBtn = ({ active, onClick, testid, icon: Icon, label }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px ${active ? "border-[#1D2951] text-[#1D2951]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
    <Icon size={14} /> {label}
  </button>
);

// =========================================================================
// BILL BOOKING TAB
// =========================================================================
function BillBookingTab() {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankBill());
  const [saving, setSaving] = useState(false);
  const [slipFor, setSlipFor] = useState(null);
  // Edit mode: id of the bill being edited (null = creating new); View mode: full bill object
  const [editId, setEditId] = useState(null);
  const [viewFor, setViewFor] = useState(null);
  // Sales-log-style multi-select + bulk "Set Company" on the Bill Booking log
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCompanyOpen, setBulkCompanyOpen] = useState(false);
  const [bulkCompanyValue, setBulkCompanyValue] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const reload = async () => {
    const [{ data: b }, { data: s }, coR] = await Promise.all([
      api.get("/bill-bookings"),
      api.get("/suppliers"),
      api.get("/companies-book").catch(() => ({ data: [] })),
    ]);
    setBills(b || []);
    setSuppliers(s || []);
    setCompanyOptions((coR.data || []).map((c) => c.name).filter(Boolean));
    setSelectedIds(new Set());
  };
  useEffect(() => { reload(); }, []);

  // Deep-link handoff from Store: if the Store screen dispatched us here to
  // book a bill for a specific GRN (see /pages/Store.jsx "PO fully received"
  // panel), pick up the GRN from sessionStorage on mount, open a fresh New
  // Bill modal, and auto-fetch the GRN to prefill supplier / bill / items.
  // sessionStorage is cleared immediately so a browser refresh doesn't
  // re-trigger the prefill.
  useEffect(() => {
    const prefill = sessionStorage.getItem("cpms_prefill_grn");
    if (prefill) {
      sessionStorage.removeItem("cpms_prefill_grn");
      // Open a fresh New Bill modal FIRST so the fetched data is visible.
      setEditId(null);
      setForm(blankBill());
      setOpen(true);
      // Fire the fetch on the next tick so the modal is mounted before the
      // fetched fields land — otherwise the modal open animation overrides
      // React's state batching and the user sees an empty form momentarily.
      setTimeout(() => { fetchGRN(prefill); }, 50);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const calc = useMemo(() => {
    const basic = Number(form.basic_amount || 0);
    const tax = Number(form.taxation || 0);
    const ded = Number(form.deduction || 0);
    const gross = basic + tax;
    return { gross_amount: gross, payable_amount: gross - ded };
  }, [form]);

  const startNew = () => { setEditId(null); setForm(blankBill()); setOpen(true); };

  const startEdit = (b) => {
    if (b.status === "paid") {
      toast.error("Cannot edit a fully-paid bill");
      return;
    }
    setEditId(b.id);
    setForm({
      supplier_id: b.supplier_id || "",
      supplier_name: b.supplier_name || "",
      bill_no: b.bill_no || "",
      bill_date: b.bill_date || todayISO(),
      basic_amount: String(b.basic_amount ?? ""),
      taxation: String(b.taxation ?? ""),
      deduction: String(b.deduction ?? ""),
      remark: b.remark || "",
      grn_number: b.grn_number || "",
      grn_date: b.grn_date || "",
      po_number: b.po_number || "",
      supplier_invoice_number: b.supplier_invoice_number || "",
      supplier_invoice_date: b.supplier_invoice_date || "",
      items_summary: b.items_summary || "",
      is_fixed_cost: !!b.is_fixed_cost,
      fixed_cost_category: b.fixed_cost_category || "",
      fixed_cost_particular: b.fixed_cost_particular || "",
      company_name: b.company_name || "",
    });
    setOpen(true);
  };

  const deleteBill = async (b) => {
    if ((b.paid_amount || 0) > 0) {
      toast.error("Cannot delete a partially-paid bill");
      return;
    }
    if (!window.confirm(`Delete bill ${b.bb_number} for ${b.supplier_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/bill-bookings/${b.id}`);
      toast.success(`${b.bb_number} deleted`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // GRN lookup → auto-prefill supplier + invoice details + items snapshot
  const [grnLookup, setGrnLookup] = useState("");
  const [grnLoading, setGrnLoading] = useState(false);
  const fetchGRN = async (grnOverride) => {
    // Guard: some callers may pass a React SyntheticEvent by mistake
    // (e.g. onClick={fetchGRN}). Only accept a string override; otherwise
    // fall back to whatever's in the input field.
    const overrideStr = typeof grnOverride === "string" ? grnOverride : "";
    const grn = (overrideStr || grnLookup || "").trim();
    if (!grn) return toast.error("Enter a GRN number");
    if (overrideStr) setGrnLookup(overrideStr);
    setGrnLoading(true);
    try {
      const { data } = await api.get(`/grn/${encodeURIComponent(grn)}`);
      setForm((prev) => ({
        ...prev,
        supplier_id: data.supplier_id || "",
        supplier_name: data.supplier_name || "",
        grn_number: data.grn_number,
        grn_date: data.grn_date || "",
        po_number: data.po_number || "",
        supplier_invoice_number: data.supplier_invoice_number || "",
        supplier_invoice_date: data.supplier_invoice_date || "",
        items_summary: data.items_summary || "",
        // Pre-fill the basic amount with the GRN's actual total — user can still override per spec
        basic_amount: prev.basic_amount || String(data.actual_total || ""),
      }));
      if (data.already_booked) {
        // Not a blocker — multiple supplier bills can share one GRN (e.g. a
        // GRN carries two items, each on its own supplier invoice). We just
        // let the user know they've booked against this GRN before.
        toast.info(`Note: A bill already exists against ${grn}. Booking another is allowed — enter the new supplier invoice details.`);
      } else {
        toast.success(`Fetched ${grn} — ${data.supplier_name || "supplier"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "GRN lookup failed");
    } finally {
      setGrnLoading(false);
    }
  };
  const save = async ({ andPrint = false } = {}) => {
    if (!form.supplier_id) return toast.error("Pick a supplier");
    if (!Number(form.basic_amount)) return toast.error("Basic amount must be > 0");
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        supplier_name: form.supplier_name,
        bill_no: form.bill_no,
        bill_date: form.bill_date || todayISO(),
        basic_amount: Number(form.basic_amount),
        taxation: Number(form.taxation || 0),
        deduction: Number(form.deduction || 0),
        remark: form.remark || "",
        grn_number: form.grn_number || "",
        grn_date: form.grn_date || "",
        po_number: form.po_number || "",
        supplier_invoice_number: form.supplier_invoice_number || "",
        supplier_invoice_date: form.supplier_invoice_date || "",
        items_summary: form.items_summary || "",
        is_fixed_cost: !!form.is_fixed_cost,
        fixed_cost_category: form.fixed_cost_category || "",
        fixed_cost_particular: form.fixed_cost_particular || "",
        company_name: (form.company_name || "").trim(),
      };
      let saved;
      if (editId) {
        await api.put(`/bill-bookings/${editId}`, payload);
        toast.success("Bill updated");
        // Re-fetch the updated doc so the print modal (if requested) has all server-computed fields
        const { data } = await api.get(`/bill-bookings/${editId}`);
        saved = data;
      } else {
        const { data } = await api.post("/bill-bookings", payload);
        toast.success(`Bill ${data.bb_number} saved`);
        saved = data;
      }
      setOpen(false);
      setEditId(null);
      reload();
      if (andPrint && saved) setSlipFor(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Search + supplier filter — matches supplier name, internal bill_no, bb_number, or supplier invoice number
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSupplierId, setFilterSupplierId] = useState("");
  const filteredBills = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return bills.filter((b) => {
      if (filterSupplierId && b.supplier_id !== filterSupplierId) return false;
      if (!q) return true;
      return (b.supplier_name || "").toLowerCase().includes(q) ||
             (b.bill_no || "").toLowerCase().includes(q) ||
             (b.bb_number || "").toLowerCase().includes(q) ||
             (b.supplier_invoice_number || "").toLowerCase().includes(q);
    });
  }, [bills, searchQuery, filterSupplierId]);
  const isFiltered = !!(searchQuery || filterSupplierId);

  return (
    <div className="space-y-4">
      {/* Running-balance panel (per supplier, per FY) — shown once the user
          picks a supplier via the filter. Same 4 KPI cards + auto/manual
          opening pattern is reused on the Customer side. */}
      {filterSupplierId && (
        <RunningBalancePanel
          kind="supplier"
          party={{
            id: filterSupplierId,
            name: (suppliers.find((s) => s.id === filterSupplierId) || {}).name || "",
          }}
        />
      )}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="text-sm font-semibold text-slate-700">Bill Booking Log <span className="text-slate-400 font-normal">({filteredBills.length}{isFiltered && `/${bills.length}`})</span></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-56">
            <SupplierPicker
              suppliers={suppliers}
              value={filterSupplierId}
              onSelect={(s) => setFilterSupplierId(s?.id || "")}
              allowClear
              placeholder="All suppliers"
              testid="bb-supplier-filter" />
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search supplier / bill no / customer bill no…"
              data-testid="bb-search"
              className={`${inputCls} pl-9 w-72`} />
          </div>
          {isFiltered && (
            <button onClick={() => { setSearchQuery(""); setFilterSupplierId(""); }}
              data-testid="bb-clear-filters"
              className="text-xs text-slate-500 hover:text-slate-900 underline">Clear</button>
          )}
          <TableExportButton
            testIdPrefix="bb-export"
            filename="bill_bookings"
            rows={filteredBills}
            dateField="bill_date"
            columns={[
              { key: "bill_no", label: "Bill No" },
              { key: "bill_date", label: "Bill Date" },
              { key: "supplier_name", label: "Supplier" },
              { key: "basic_amount", label: "Basic" },
              { key: "taxation", label: "Tax" },
              { key: "gross_amount", label: "Gross" },
              { key: "deduction", label: "Deduction" },
              { key: "payable_amount", label: "Payable" },
              { key: "paid_amount", label: "Paid" },
              { key: "grn_number", label: "GRN" },
              { key: "po_number", label: "PO" },
              { key: "is_fixed_cost", label: "Fixed Cost" },
              { key: "fixed_cost_category", label: "Fixed Cat" },
              { key: "remark", label: "Remark" },
            ]}
          />
          <button
            onClick={async () => {
              if (!window.confirm("Auto-tag every un-tagged bill by inheriting the company from its linked GRN? Safe to re-run.")) return;
              try {
                const { data } = await api.post("/bill-bookings/inherit-company-from-grn");
                toast.success(`${data.updated} bill${data.updated === 1 ? "" : "s"} tagged. ${data.already_tagged} were already tagged. ${data.skipped_no_grn + data.skipped_no_source} skipped (no linked GRN or GRN itself untagged).`);
                reload();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Backfill failed");
              }
            }}
            data-testid="bb-inherit-from-grn"
            className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
            title="One-shot: copy company from each linked GRN's store entry to every un-tagged bill"
          >
            Auto-tag from GRN
          </button>
          <button onClick={startNew} data-testid="bb-add-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
            <Plus size={14} /> New Bill
          </button>
        </div>
      </div>

      {/* Bulk toolbar — shows when at least one bill is ticked */}
      {selectedIds.size > 0 && (
        <div
          className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-md px-3 py-1.5"
          data-testid="bb-bulk-bar"
        >
          <span className="text-xs font-semibold text-emerald-800">
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => { setBulkCompanyValue(""); setBulkCompanyOpen(true); }}
            data-testid="bb-bulk-set-company"
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1 rounded-md"
          >
            Set Company
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            data-testid="bb-bulk-clear"
            className="text-xs text-slate-600 hover:text-slate-900"
          >Clear</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={filteredBills.length > 0 && filteredBills.every((b) => selectedIds.has(b.id))}
                  onChange={() => {
                    setSelectedIds((prev) => {
                      const allSelected = filteredBills.length > 0 && filteredBills.every((b) => prev.has(b.id));
                      const next = new Set(prev);
                      if (allSelected) filteredBills.forEach((b) => next.delete(b.id));
                      else filteredBills.forEach((b) => next.add(b.id));
                      return next;
                    });
                  }}
                  data-testid="bb-select-all"
                  className="cursor-pointer"
                />
              </th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Voucher #</th>
              <th className="px-3 py-2 text-left">Bill No.</th>
              <th className="px-3 py-2 text-left">Company</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-right">Basic</th>
              <th className="px-3 py-2 text-right">Tax</th>
              <th className="px-3 py-2 text-right">Gross</th>
              <th className="px-3 py-2 text-right">Deduction</th>
              <th className="px-3 py-2 text-right">Payable</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBills.length === 0 ? (
              <tr><td colSpan={13} className="text-center py-6 text-slate-400 italic">
                {searchQuery ? `No bills match "${searchQuery}"` : "No bills booked yet"}
              </td></tr>
            ) : filteredBills.map((b) => {
              const isSelected = selectedIds.has(b.id);
              return (
              <tr key={b.id} className={`border-t border-slate-100 hover:bg-slate-50 ${isSelected ? "bg-emerald-50/70" : ""}`}>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(b.id)) next.delete(b.id); else next.add(b.id);
                        return next;
                      });
                    }}
                    data-testid={`bb-select-${b.bb_number}`}
                    className="cursor-pointer"
                  />
                </td>
                <td className="px-3 py-2 font-mono text-xs">{b.date}</td>
                <td className="px-3 py-2 font-mono font-semibold">{b.bb_number}</td>
                <td className="px-3 py-2 font-mono text-xs">{b.bill_no || "—"}</td>
                <td className="px-3 py-2 text-[11px]">
                  {b.company_name
                    ? <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5 font-medium">{b.company_name}</span>
                    : <span className="text-slate-300 italic">Untagged</span>}
                </td>
                <td className="px-3 py-2">{b.supplier_name}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(b.basic_amount)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(b.taxation)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(b.gross_amount)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(b.deduction)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(b.payable_amount)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${b.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {b.status?.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="inline-flex items-center gap-0.5">
                    <button onClick={() => setViewFor(b)} data-testid={`bb-view-${b.bb_number}`}
                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded" title="View">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => startEdit(b)} data-testid={`bb-edit-${b.bb_number}`}
                      disabled={b.status === "paid"}
                      className="p-1.5 text-amber-700 hover:bg-amber-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title={b.status === "paid" ? "Paid bills can't be edited" : "Edit"}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteBill(b)} data-testid={`bb-delete-${b.bb_number}`}
                      disabled={(b.paid_amount || 0) > 0}
                      className="p-1.5 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title={(b.paid_amount || 0) > 0 ? "Paid/partial bills can't be deleted" : "Delete"}>
                      <Trash2 size={14} />
                    </button>
                    <button onClick={() => setSlipFor(b)} data-testid={`bb-print-${b.bb_number}`}
                      className="p-1.5 text-[#1D2951] hover:bg-slate-100 rounded" title="Print slip">
                      <Printer size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* ===== Bulk Set Company modal ===== */}
      {bulkCompanyOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBulkCompanyOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-900">
                Set Company for {selectedIds.size} bill{selectedIds.size === 1 ? "" : "s"}
              </h3>
              <button onClick={() => setBulkCompanyOpen(false)} className="text-slate-400 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="text-xs text-slate-500 mb-2">
              The tagged company determines which sub-company these bills fall under on MR &amp; Accounts Dashboard.
            </div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Company</label>
            <select
              value={bulkCompanyValue}
              onChange={(e) => setBulkCompanyValue(e.target.value)}
              data-testid="bb-bulk-company-select"
              className={inputCls}
            >
              <option value="">— Clear tag (leave unassigned) —</option>
              {companyOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setBulkCompanyOpen(false)}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-md"
                data-testid="bb-bulk-cancel"
              >Cancel</button>
              <button
                onClick={async () => {
                  setBulkSaving(true);
                  try {
                    const { data } = await api.post("/bill-bookings/bulk-set-company", {
                      bill_ids: Array.from(selectedIds),
                      company_name: bulkCompanyValue,
                    });
                    toast.success(
                      bulkCompanyValue
                        ? `Tagged ${data.updated} bill${data.updated === 1 ? "" : "s"} → ${bulkCompanyValue}`
                        : `Cleared company on ${data.updated} bill${data.updated === 1 ? "" : "s"}`
                    );
                    setBulkCompanyOpen(false);
                    setBulkCompanyValue("");
                    reload();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Bulk update failed");
                  } finally {
                    setBulkSaving(false);
                  }
                }}
                disabled={bulkSaving}
                data-testid="bb-bulk-confirm"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md"
              >{bulkSaving ? "Saving…" : (bulkCompanyValue ? "Apply Tag" : "Clear Tag")}</button>
            </div>
          </div>
        </div>
      )}

      {/* New / Edit Bill modal */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setOpen(false); setEditId(null); }}>
          <div className="bg-white rounded-lg max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{editId ? "Edit Bill Booking" : "New Bill Booking"}</h2>
              <button onClick={() => { setOpen(false); setEditId(null); }} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
            </div>

            {/* ===== Fetch by GRN ===== */}
            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
              <div className="text-xs font-semibold text-slate-700 mb-1.5">Fetch by GRN <span className="text-slate-400 font-normal">— prefills supplier, invoice, items</span></div>
              <div className="flex gap-2">
                <input value={grnLookup} onChange={(e) => setGrnLookup(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchGRN(); }}
                  placeholder="Enter GRN number (e.g. GRN-0001)"
                  data-testid="bb-grn-lookup"
                  className={`${inputCls} font-mono`} />
                <button onClick={() => fetchGRN()} disabled={grnLoading} data-testid="bb-grn-fetch"
                  className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md whitespace-nowrap">
                  {grnLoading ? "Fetching…" : "Fetch"}
                </button>
              </div>
              {form.grn_number && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <Info label="GRN No" value={form.grn_number} />
                  <Info label="GRN Date" value={form.grn_date || "—"} />
                  <Info label="PO Number" value={form.po_number || "—"} />
                  <Info label="Items" value={form.items_summary || "—"} className="col-span-3" />
                </div>
              )}
            </div>

            {/* ===== Fixed Cost toggle — rent, utilities, payroll, etc ===== */}
            <div className={`border rounded-lg p-3 mb-4 ${form.is_fixed_cost ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.is_fixed_cost}
                  onChange={(e) => setForm({ ...form, is_fixed_cost: e.target.checked })}
                  data-testid="bb-fixed-cost-toggle"
                  className="h-4 w-4 accent-amber-600" />
                Fixed Cost <span className="text-slate-400 font-normal">(non-GRN bill — rent, electricity, professional fees, etc.)</span>
              </label>
              {form.is_fixed_cost && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Lbl label="Category *">
                    <input value={form.fixed_cost_category}
                      onChange={(e) => setForm({ ...form, fixed_cost_category: e.target.value })}
                      placeholder="e.g. Rent, Electricity, Internet"
                      data-testid="bb-fixed-cost-category"
                      className={inputCls} />
                  </Lbl>
                  <Lbl label="Particular">
                    <input value={form.fixed_cost_particular}
                      onChange={(e) => setForm({ ...form, fixed_cost_particular: e.target.value })}
                      placeholder="Free-text — e.g. Office rent for May 2026"
                      data-testid="bb-fixed-cost-particular"
                      className={inputCls} />
                  </Lbl>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Company <span className="text-slate-400 font-normal">(sub-entity — required for correct MR / Accounts split)</span></label>
                <select
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  data-testid="bb-form-company"
                  className={inputCls}
                >
                  <option value="">— Unassigned (visible only under &quot;All Companies&quot;) —</option>
                  {companyOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Supplier <span className="text-rose-500">*</span></label>
                <SupplierPicker suppliers={suppliers} value={form.supplier_id}
                  onSelect={(s) => setForm({ ...form, supplier_id: s.id, supplier_name: s.supplier_name })} />
              </div>
              <Lbl label="Supplier Invoice No.">
                <input value={form.supplier_invoice_number}
                  onChange={(e) => setForm({ ...form, supplier_invoice_number: e.target.value })}
                  className={inputCls} data-testid="bb-supplier-invoice-no" />
              </Lbl>
              <Lbl label="Supplier Invoice Date">
                <input type="date" value={form.supplier_invoice_date}
                  onChange={(e) => setForm({ ...form, supplier_invoice_date: e.target.value })}
                  className={inputCls} data-testid="bb-supplier-invoice-date" />
              </Lbl>
              <Lbl label="Bill No. (internal ref)"><input value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} className={inputCls} data-testid="bb-bill-no" /></Lbl>
              <Lbl label="Bill Date"><input type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} className={inputCls} data-testid="bb-bill-date" /></Lbl>
              <Lbl label="Actual / Basic Amount *"><input type="number" step="any" value={form.basic_amount} onChange={(e) => setForm({ ...form, basic_amount: e.target.value })} className={monoCls} data-testid="bb-basic" /></Lbl>
              <Lbl label="GST / Taxation"><input type="number" step="any" value={form.taxation} onChange={(e) => setForm({ ...form, taxation: e.target.value })} className={monoCls} data-testid="bb-tax" /></Lbl>
              <Lbl label="Deduction"><input type="number" step="any" value={form.deduction} onChange={(e) => setForm({ ...form, deduction: e.target.value })} className={monoCls} data-testid="bb-deduction" /></Lbl>
              <Lbl label="Gross / Payable (auto)">
                <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-base font-mono text-right font-bold text-emerald-700" data-testid="bb-payable">
                  ₹ {fmtINR(calc.payable_amount)}
                </div>
                <div className="text-[10px] text-slate-400 text-right mt-0.5">Actual + GST − Deduction</div>
              </Lbl>
              <Lbl label="Remark" className="col-span-2"><input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} className={inputCls} /></Lbl>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setOpen(false); setEditId(null); }} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button onClick={() => save({ andPrint: true })} disabled={saving} data-testid="bb-save-print"
                className="border border-[#1D2951] text-[#1D2951] hover:bg-[#1D2951] hover:text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                <Printer size={14} /> {editId ? "Update & Print Slip" : "Save & Generate Slip"}
              </button>
              <button onClick={() => save()} disabled={saving} data-testid="bb-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                <Save size={14} /> {saving ? "Saving…" : (editId ? "Update Bill" : "Book Bill")}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewFor && <BillViewModal bill={viewFor} onClose={() => setViewFor(null)} onPrint={() => { setSlipFor(viewFor); setViewFor(null); }} />}

      {slipFor && <BillSlipModal bill={slipFor} onClose={() => setSlipFor(null)} />}
    </div>
  );
}

const blankBill = () => ({
  supplier_id: "", supplier_name: "", bill_no: "", bill_date: todayISO(),
  basic_amount: "", taxation: "", deduction: "", remark: "",
  // GRN-linked fields (optional — blank when booking a plain bill)
  grn_number: "", grn_date: "", po_number: "",
  supplier_invoice_number: "", supplier_invoice_date: "",
  items_summary: "",
  // Fixed-cost mode (rent / utilities / payroll etc — no GRN linkage)
  is_fixed_cost: false,
  fixed_cost_category: "",
  fixed_cost_particular: "",
  // Sub-company tag — drives which sub-company this bill counts under on MR
  company_name: "",
});

const Info = ({ label, value, className = "" }) => (
  <div className={`bg-white border border-slate-200 rounded px-2 py-1.5 ${className}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className="text-xs font-medium text-slate-800 truncate" title={String(value || "")}>{value || "—"}</div>
  </div>
);

const Lbl = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
    {children}
  </div>
);

function SupplierPicker({ suppliers, value, onSelect, allowClear = false, placeholder = "Search supplier…", testid = "bb-supplier" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = suppliers.filter((s) =>
    !q || `${s.supplier_name} ${s.supplier_code || ""}`.toLowerCase().includes(q.toLowerCase())
  );
  const selected = suppliers.find((s) => s.id === value);
  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        value={open ? q : (selected?.supplier_name || "")}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setQ(""); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        data-testid={testid}
        className={`${inputCls} pl-9 ${allowClear && selected ? "pr-8" : ""}`}
      />
      {allowClear && selected && !open && (
        <button type="button"
          onMouseDown={(e) => { e.preventDefault(); onSelect(null); }}
          data-testid={`${testid}-clear`}
          title="Clear"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
          <X size={14} />
        </button>
      )}
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">No suppliers</div>
          ) : filtered.map((s) => (
            <button key={s.id} type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onSelect(s); setOpen(false); setQ(""); }}
              data-testid={`${testid}-opt-${s.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
              <div className="font-medium">{s.supplier_name}</div>
              <div className="text-[10px] text-slate-500 font-mono">{s.supplier_code}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function BillViewModal({ bill, onClose, onPrint }) {
  const b = bill || {};
  const Row = ({ label, value, mono = false, accent = false }) => (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-b-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-xs ${mono ? "font-mono" : ""} ${accent ? "font-bold text-emerald-700" : "text-slate-900"}`}>{value || "—"}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bb-view-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Bill Booking</div>
            <h2 className="text-lg font-semibold font-mono">{b.bb_number}</h2>
            <div className="text-xs text-slate-500">{b.date}{b.created_by ? ` · by ${b.created_by}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${b.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {(b.status || "open").toUpperCase()}
            </span>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
          </div>
        </div>

        <section className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Supplier</div>
          <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <div className="text-sm font-semibold text-slate-900">{b.supplier_name}</div>
          </div>
        </section>

        {(b.grn_number || b.po_number || b.supplier_invoice_number) && (
          <section className="mb-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">GRN / PO Reference</div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 space-y-1">
              <Row label="GRN No" value={b.grn_number} mono />
              <Row label="GRN Date" value={b.grn_date} mono />
              <Row label="PO Number" value={b.po_number} mono />
              <Row label="Supplier Invoice No" value={b.supplier_invoice_number} mono />
              <Row label="Supplier Invoice Date" value={b.supplier_invoice_date} mono />
              <Row label="Items" value={b.items_summary} />
            </div>
          </section>
        )}

        <section className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Bill Details</div>
          <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 space-y-1">
            <Row label="Internal Bill No" value={b.bill_no} mono />
            <Row label="Bill Date" value={b.bill_date} mono />
            <Row label="Basic Amount" value={`₹ ${fmtINR(b.basic_amount)}`} mono />
            <Row label="GST / Taxation" value={`₹ ${fmtINR(b.taxation)}`} mono />
            <Row label="Gross Amount" value={`₹ ${fmtINR(b.gross_amount)}`} mono />
            <Row label="Deduction" value={`₹ ${fmtINR(b.deduction)}`} mono />
            <Row label="Payable Amount" value={`₹ ${fmtINR(b.payable_amount)}`} mono accent />
            <Row label="Paid So Far" value={`₹ ${fmtINR(b.paid_amount)}`} mono />
          </div>
        </section>

        {b.remark && (
          <section className="mb-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Remark</div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-700">{b.remark}</div>
          </section>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md" data-testid="bb-view-close">Close</button>
          <button onClick={onPrint} data-testid="bb-view-print"
            className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
            <Printer size={14} /> Print Slip
          </button>
        </div>
      </div>
    </div>
  );
}

function BillSlipModal({ bill, onClose }) {
  const print = () => window.print();
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 print:p-0 print:bg-white" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 print:rounded-none print:shadow-none" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 print:hidden">
          <h2 className="text-lg font-semibold">Bill Slip</h2>
          <div className="flex gap-2">
            <button onClick={print} className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
              <Printer size={14} /> Print
            </button>
            <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Close</button>
          </div>
        </div>
        <div className="border-2 border-slate-900 p-6">
          <div className="text-center border-b border-slate-300 pb-3 mb-4">
            <div className="text-xl font-bold tracking-wide">BILL BOOKING SLIP</div>
            <div className="text-xs text-slate-600 mt-1">{bill.bb_number} · {bill.date}</div>
          </div>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <KV label="Supplier" value={bill.supplier_name} bold />
            <KV label="Bill No." value={bill.bill_no || "—"} />
            <KV label="Bill Date" value={bill.bill_date || bill.date} />
            <KV label="Basic Amount" value={`₹ ${fmtINR(bill.basic_amount)}`} mono />
            <KV label="Taxation" value={`₹ ${fmtINR(bill.taxation)}`} mono />
            <KV label="Gross Amount" value={`₹ ${fmtINR(bill.gross_amount)}`} mono bold />
            <KV label="Deduction" value={`₹ ${fmtINR(bill.deduction)}`} mono />
            <KV label="Payable Amount" value={`₹ ${fmtINR(bill.payable_amount)}`} mono bold big />
            {bill.remark && <KV label="Remark" value={bill.remark} full />}
          </div>
        </div>
      </div>
    </div>
  );
}

const KV = ({ label, value, bold, mono, big, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`${bold ? "font-bold" : ""} ${mono ? "font-mono" : ""} ${big ? "text-lg" : ""}`}>{value}</div>
  </div>
);

// =========================================================================
// PAYMENT TAB
// =========================================================================
function PaymentTab() {
  const [target, setTarget] = useState("supplier"); // 'supplier' | 'customer' | 'bank_reco'
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="radio" name="pay-target" value="supplier" checked={target === "supplier"}
            onChange={() => setTarget("supplier")} data-testid="pay-target-supplier" />
          Supplier
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="radio" name="pay-target" value="customer" checked={target === "customer"}
            onChange={() => setTarget("customer")} data-testid="pay-target-customer" />
          Customer
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="radio" name="pay-target" value="bank_reco" checked={target === "bank_reco"}
            onChange={() => setTarget("bank_reco")} data-testid="pay-target-bank-reco" />
          Bank Reco
        </label>
      </div>
      {target === "supplier" && <SupplierPaymentFlow />}
      {target === "customer" && <CustomerPaymentFlow />}
      {target === "bank_reco" && <BankRecoFlow />}
    </div>
  );
}

function SupplierPaymentFlow() {
  const [suppliers, setSuppliers] = useState([]);
  const [selSupplier, setSelSupplier] = useState(null);
  const [bills, setBills] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [selBillIds, setSelBillIds] = useState([]);
  const [banks, setBanks] = useState([]);
  const [payForm, setPayForm] = useState(null); // null or full form
  const [saving, setSaving] = useState(false);
  const [voucherFor, setVoucherFor] = useState(null);
  const [billSearch, setBillSearch] = useState("");      // search within the supplier's bills
  const [voucherSearch, setVoucherSearch] = useState(""); // search within past vouchers

  useEffect(() => {
    api.get("/suppliers").then(({ data }) => setSuppliers(data || []));
    api.get("/banks").then(({ data }) => setBanks(data || []));
  }, []);

  const loadSupplier = async (s) => {
    setSelSupplier(s);
    setSelBillIds([]);
    setPayForm(null);
    const [{ data: bb }, { data: vv }] = await Promise.all([
      api.get(`/bill-bookings?supplier_id=${s.id}`),
      api.get(`/supplier-payments?supplier_id=${s.id}`),
    ]);
    setBills(bb || []);
    setVouchers(vv || []);
  };

  // Sum of OUTSTANDING amounts on selected bills (not payable, so partials
  // don't double-count what's already been paid). Falls back to
  // payable_amount when outstanding_amount is missing (legacy bills).
  const totalSelected = useMemo(() =>
    bills.filter((b) => selBillIds.includes(b.id))
      .reduce((sum, b) => {
        const outstanding = b.outstanding_amount != null
          ? Number(b.outstanding_amount)
          : Number(b.payable_amount || 0) - Number(b.paid_amount || 0);
        return sum + Math.max(0, outstanding);
      }, 0)
  , [bills, selBillIds]);

  const filteredBills = useMemo(() => {
    const q = billSearch.trim().toLowerCase();
    if (!q) return bills;
    return bills.filter((b) =>
      (b.bill_no || "").toLowerCase().includes(q) ||
      (b.bb_number || "").toLowerCase().includes(q)
    );
  }, [bills, billSearch]);

  const filteredVouchers = useMemo(() => {
    const q = voucherSearch.trim().toLowerCase();
    if (!q) return vouchers;
    return vouchers.filter((v) =>
      (v.voucher_no || "").toLowerCase().includes(q) ||
      (v.check_number || "").toLowerCase().includes(q) ||
      (v.bills || []).some((x) => (x.bb_number || "").toLowerCase().includes(q) || (x.bill_no || "").toLowerCase().includes(q))
    );
  }, [vouchers, voucherSearch]);

  const openPay = () => {
    if (!selBillIds.length) return toast.error("Select at least one open bill");
    setPayForm({
      _editingId: null,         // null = new voucher, otherwise the voucher id
      _editingVoucherNo: "",
      bank_id: "", bank_account_number: "", bank_name: "",
      debit_amount: 0,
      // User-entered cash amount actually disbursed. NOT defaulted — the form
      // forces an explicit value so users don't accidentally mark a partial as
      // full settlement.
      paid_amount: "",
      check_number: "", check_date: todayISO(),
      remark: "",
    });
  };

  const save = async () => {
    const payable = totalSelected || 0;
    if (payForm.paid_amount === "" || payForm.paid_amount == null) {
      return toast.error("Enter the amount actually paid");
    }
    if (!payForm.bank_id) return toast.error("Pick a bank account");
    const requestedPaid = Number(payForm.paid_amount);
    if (requestedPaid < 0) return toast.error("Paid amount cannot be negative");
    if (requestedPaid > payable + 0.01) return toast.error(`Paid amount cannot exceed payable (₹${fmtINR(payable)})`);
    setSaving(true);
    try {
      const body = {
        bill_booking_ids: selBillIds,
        supplier_id: selSupplier.id,
        supplier_name: selSupplier.supplier_name,
        bank_id: payForm.bank_id,
        bank_account_number: payForm.bank_account_number,
        bank_name: payForm.bank_name,
        payable_amount: payable,
        debit_amount: Number(payForm.debit_amount || 0),
        paid_amount: requestedPaid,
        check_number: payForm.check_number,
        check_date: payForm.check_date,
        remark: payForm.remark,
      };
      const { data } = payForm._editingId
        ? await api.put(`/supplier-payments/${payForm._editingId}`, body)
        : await api.post("/supplier-payments", body);
      toast.success(
        payForm._editingId
          ? `Voucher ${data.voucher_no} updated`
          : data.is_partial
            ? `Voucher ${data.voucher_no} (PARTIAL — ₹${fmtINR(data.paid_amount)}) created`
            : `Voucher ${data.voucher_no} created`
      );
      setPayForm(null);
      loadSupplier(selSupplier);
      setVoucherFor(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment save failed");
    } finally {
      setSaving(false);
    }
  };

  const editVoucher = (v) => {
    // Re-check the bills this voucher touched so the form can re-allocate.
    const ids = (v.bill_booking_ids || []).filter(Boolean);
    setSelBillIds(ids);
    setPayForm({
      _editingId: v.id,
      _editingVoucherNo: v.voucher_no,
      bank_id: v.bank_id || "",
      bank_account_number: v.bank_account_number || "",
      bank_name: v.bank_name || "",
      debit_amount: Number(v.debit_amount || 0),
      paid_amount: String(v.paid_amount != null ? v.paid_amount : v.net_payable_amount || ""),
      check_number: v.check_number || "",
      check_date: v.check_date || todayISO(),
      remark: v.remark || "",
    });
  };

  const deleteVoucher = async (v) => {
    if (!window.confirm(`Delete voucher ${v.voucher_no}?\n\nThis will reverse ₹${fmtINR(v.paid_amount || v.net_payable_amount)} from the affected bill(s). The voucher number won't be reused.`)) return;
    try {
      await api.delete(`/supplier-payments/${v.id}`);
      toast.success(`Voucher ${v.voucher_no} deleted; bill state reversed`);
      loadSupplier(selSupplier);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const net = (totalSelected || 0) - Number(payForm?.debit_amount || 0);

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Pick Supplier</label>
        <SupplierPicker suppliers={suppliers} value={selSupplier?.id}
          onSelect={loadSupplier} />
      </div>

      {selSupplier && (
        <>
          {/* ===== Running balance panel — 4 KPI cards (Opening / Purchase / Paid / Closing) ===== */}
          <RunningBalancePanel kind="supplier" party={{ id: selSupplier.id, name: selSupplier.supplier_name }} />

          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex justify-between items-center gap-2 flex-wrap">
              <span>Bill Bookings for {selSupplier.supplier_name} <span className="text-slate-400 font-normal">({filteredBills.length}{billSearch && `/${bills.length}`})</span></span>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input value={billSearch} onChange={(e) => setBillSearch(e.target.value)}
                    placeholder="Search bill no…" data-testid="sp-bill-search"
                    className="pl-7 pr-2 py-1 border border-slate-300 rounded text-xs w-48" />
                </div>
                <button onClick={openPay} disabled={!selBillIds.length} data-testid="pay-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                  <IndianRupee size={12} /> Pay ({selBillIds.length})
                </button>
              </div>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Voucher #</th>
                  <th className="px-3 py-2 text-left">Bill No</th>
                  <th className="px-3 py-2 text-right">Payable</th>
                  <th className="px-3 py-2 text-right text-emerald-700">Paid</th>
                  <th className="px-3 py-2 text-right text-rose-700">Outstanding</th>
                  <th className="px-3 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredBills.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-6 text-slate-400 italic">{billSearch ? `No bills match "${billSearch}"` : "No bills for this supplier"}</td></tr>
                ) : filteredBills.map((b) => {
                  const isOpen = b.status !== "paid";
                  const paid = Number(b.paid_amount || 0);
                  const outstanding = b.outstanding_amount != null
                    ? Number(b.outstanding_amount)
                    : Math.max(0, Number(b.payable_amount || 0) - paid);
                  // Binary status. Legacy "open" / "partial" values from
                  // pre-iter-12 docs are coerced to "pending" here.
                  const isPaid = b.status === "paid";
                  const statusLabel = isPaid ? "PAID" : "PENDING";
                  const statusClr = isPaid
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700";
                  return (
                    <tr key={b.id} className={`border-t border-slate-100 ${isOpen ? "hover:bg-slate-50" : "opacity-60"}`}>
                      <td className="px-3 py-2">
                        {isOpen && (
                          <input type="checkbox" checked={selBillIds.includes(b.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelBillIds([...selBillIds, b.id]);
                              else setSelBillIds(selBillIds.filter((x) => x !== b.id));
                            }}
                            data-testid={`pay-bill-check-${b.bb_number}`} />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{b.date}</td>
                      <td className="px-3 py-2 font-mono">{b.bb_number}</td>
                      <td className="px-3 py-2">{b.bill_no || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(b.payable_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700" data-testid={`bill-paid-${b.bb_number}`}>
                        {paid > 0 ? `₹${fmtINR(paid)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-rose-700" data-testid={`bill-outstanding-${b.bb_number}`}>
                        {outstanding > 0 ? `₹${fmtINR(outstanding)}` : "₹0"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${statusClr}`} data-testid={`bill-status-${b.bb_number}`}>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {vouchers.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex justify-between items-center gap-2 flex-wrap">
                <span>Past Vouchers <span className="text-slate-400 font-normal">({filteredVouchers.length}{voucherSearch && `/${vouchers.length}`})</span></span>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input value={voucherSearch} onChange={(e) => setVoucherSearch(e.target.value)}
                    placeholder="Search voucher / bill / cheque…" data-testid="sp-voucher-search"
                    className="pl-7 pr-2 py-1 border border-slate-300 rounded text-xs w-60" />
                </div>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Voucher #</th>
                    <th className="px-3 py-2 text-left">Bills</th>
                    <th className="px-3 py-2 text-right">Net Payable</th>
                    <th className="px-3 py-2 text-right text-emerald-700">Paid</th>
                    <th className="px-3 py-2 text-left">Check #</th>
                    <th className="px-3 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVouchers.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-6 text-slate-400 italic">No vouchers match "{voucherSearch}"</td></tr>
                  ) : filteredVouchers.map((v) => {
                    // `paid_amount` is the cash actually disbursed; legacy
                    // voucher rows (pre-partial-payment fix) don't have it —
                    // those were always full settlements, so fall back to
                    // net_payable_amount for display continuity.
                    const paid = v.paid_amount != null ? Number(v.paid_amount) : Number(v.net_payable_amount || 0);
                    return (
                    <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs">{v.date}</td>
                      <td className="px-3 py-2 font-mono font-semibold">
                        {v.voucher_no}
                        {v.is_partial && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-100 text-blue-700 align-middle" data-testid={`voucher-partial-${v.voucher_no}`}>PARTIAL</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">{(v.bills || []).map((x) => x.bb_number).join(", ")}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtINR(v.net_payable_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700" data-testid={`voucher-paid-${v.voucher_no}`}>₹{fmtINR(paid)}</td>
                      <td className="px-3 py-2 font-mono">{v.check_number || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => editVoucher(v)} data-testid={`voucher-edit-${v.voucher_no}`}
                            className="p-1.5 text-slate-600 hover:bg-slate-100 rounded" title="Edit voucher">
                            <Edit size={14} />
                          </button>
                          <button onClick={() => deleteVoucher(v)} data-testid={`voucher-delete-${v.voucher_no}`}
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" title="Delete & reverse bill state">
                            <Trash2 size={14} />
                          </button>
                          <button onClick={() => setVoucherFor(v)} data-testid={`voucher-print-${v.voucher_no}`}
                            className="p-1.5 text-[#1D2951] hover:bg-slate-100 rounded" title="Voucher slip">
                            <Printer size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {payForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayForm(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {payForm._editingId ? `Edit Voucher — ${payForm._editingVoucherNo}` : "Pay Supplier"}
              </h2>
              <button onClick={() => setPayForm(null)} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="Supplier" className="col-span-2">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-semibold">{selSupplier.supplier_name}</div>
              </Lbl>
              <Lbl label="Bank Account *" className="col-span-2">
                <BankPicker banks={banks} value={payForm.bank_id}
                  onSelect={(b) => setPayForm({ ...payForm, bank_id: b.id, bank_account_number: b.account_number, bank_name: b.bank_name })} />
              </Lbl>
              {/* ===== Payment math — payable, paid (user input), outstanding (auto) ===== */}
              <Lbl label="Payable Amount (auto)">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono text-right" data-testid="pay-payable">
                  ₹ {fmtINR(totalSelected)}
                </div>
              </Lbl>
              <Lbl label="Paid Amount * (required)">
                <input type="number" step="any" value={payForm.paid_amount}
                  onChange={(e) => setPayForm({ ...payForm, paid_amount: e.target.value })}
                  placeholder="0.00"
                  className={`${monoCls} border-[#1D2951]`} data-testid="pay-paid-amount" autoFocus={!payForm._editingId} />
              </Lbl>
              <Lbl label="Outstanding After Payment (auto)" className="col-span-2">
                {(() => {
                  const paidN = Number(payForm.paid_amount || 0);
                  const outAfter = Math.max(0, (totalSelected || 0) - paidN);
                  const isFull = outAfter <= 0.01 && paidN > 0;
                  return (
                    <div className={`px-3 py-2 border rounded-md text-base font-mono text-right font-bold ${isFull ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="pay-outstanding-preview">
                      ₹ {fmtINR(outAfter)} <span className="text-xs font-semibold uppercase">{isFull ? "→ PAID" : "→ PENDING"}</span>
                    </div>
                  );
                })()}
              </Lbl>
              <Lbl label="Debit / TDS (optional, recorded but does NOT settle the bill)" className="col-span-2">
                <input type="number" step="any" value={payForm.debit_amount}
                  onChange={(e) => setPayForm({ ...payForm, debit_amount: e.target.value })}
                  className={monoCls} placeholder="0.00" data-testid="pay-debit" />
              </Lbl>
              <Lbl label="Check Number"><input value={payForm.check_number} onChange={(e) => setPayForm({ ...payForm, check_number: e.target.value })} className={inputCls} data-testid="pay-check-no" /></Lbl>
              <Lbl label="Check Date"><input type="date" value={payForm.check_date} onChange={(e) => setPayForm({ ...payForm, check_date: e.target.value })} className={inputCls} data-testid="pay-check-date" /></Lbl>
              <Lbl label="Remark" className="col-span-2"><input value={payForm.remark} onChange={(e) => setPayForm({ ...payForm, remark: e.target.value })} className={inputCls} /></Lbl>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPayForm(null)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="pay-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                <Save size={14} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {voucherFor && <VoucherSlipModal voucher={voucherFor} supplier={selSupplier} onClose={() => setVoucherFor(null)} />}
    </div>
  );
}

function BankPicker({ banks, value, onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = banks.filter((b) =>
    !q || `${b.bank_name} ${b.account_number}`.toLowerCase().includes(q.toLowerCase())
  );
  const selected = banks.find((b) => b.id === value);
  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input value={open ? q : (selected ? `${selected.bank_name} · ${selected.account_number}` : "")}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setQ(""); setOpen(true); }}
        placeholder="Search bank…"
        data-testid="pay-bank"
        className={`${inputCls} pl-9`} />
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">No banks — add in Bank Master</div>
          ) : filtered.map((b) => (
            <button key={b.id} type="button" onClick={() => { onSelect(b); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
              <div className="font-medium">{b.bank_name}</div>
              <div className="text-[10px] text-slate-500 font-mono">{b.account_number} · {b.account_type}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VoucherSlipModal({ voucher, supplier, onClose }) {
  const print = () => window.print();
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 print:p-0 print:bg-white" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 print:rounded-none print:shadow-none" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 print:hidden">
          <h2 className="text-lg font-semibold">Voucher Slip</h2>
          <div className="flex gap-2">
            <button onClick={print} className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
              <Printer size={14} /> Print
            </button>
            <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Close</button>
          </div>
        </div>
        <div className="border-2 border-slate-900 p-6">
          <div className="text-center border-b border-slate-300 pb-3 mb-4">
            <div className="text-xl font-bold tracking-wide">PAYMENT VOUCHER SLIP</div>
            <div className="text-xs text-slate-600 mt-1">{voucher.voucher_no} · {voucher.date}</div>
          </div>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <KV label="Supplier" value={voucher.supplier_name || supplier?.supplier_name} bold full />
            <KV label="Check Number" value={voucher.check_number || "—"} mono />
            <KV label="Check Date" value={voucher.check_date || "—"} mono />
            <KV label="Bank" value={voucher.bank_name || "—"} />
            <KV label="Bank A/c" value={voucher.bank_account_number || "—"} mono />
            <KV label="Supplier Bill Numbers" full
              value={(voucher.bills || []).map((b) => `${b.bb_number}${b.bill_no ? " (" + b.bill_no + ")" : ""}`).join(", ") || "—"} />
            <KV label="Payable Amount" value={`₹ ${fmtINR(voucher.payable_amount)}`} mono />
            <KV label="Debit Amount" value={`₹ ${fmtINR(voucher.debit_amount)}`} mono />
            <KV label="Net Payable" value={`₹ ${fmtINR(voucher.net_payable_amount)}`} mono />
            <KV
              label={voucher.is_partial ? "Paid (PARTIAL)" : "Check Amount (Paid)"}
              value={`₹ ${fmtINR(voucher.paid_amount != null ? voucher.paid_amount : voucher.net_payable_amount)}`}
              mono bold big full
            />
            {voucher.is_partial && (
              <KV
                label="Outstanding After This Payment"
                value={`₹ ${fmtINR(Number(voucher.net_payable_amount || 0) - Number(voucher.paid_amount || 0))}`}
                mono full
              />
            )}
            {voucher.remark && <KV label="Remark" value={voucher.remark} full />}
          </div>
        </div>
      </div>
    </div>
  );
}


// =========================================================================
// CUSTOMER PAYMENT FLOW
// =========================================================================
function CustomerPaymentFlow() {
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [invoices, setInvoices] = useState([]);     // invoices for the picked customer
  const [payments, setPayments] = useState([]);     // past receipts for the picked customer
  const [selCustomer, setSelCustomer] = useState(null);
  const [draft, setDraft] = useState(blankCustomerPayment());
  const [saving, setSaving] = useState(false);
  const [selInvIds, setSelInvIds] = useState([]);   // checked invoice ids this payment settles
  // Manual entry inputs — user types Paid / Deduction / Discount; these drive the Amount field below.
  const [manualEntry, setManualEntry] = useState({
    paid_amount: "",
    deduction_amount: "",
    discount_input: "",     // raw input — could be a value OR a percentage
    discount_mode: "value", // "value" | "pct"
  });

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data || []));
    api.get("/banks").then(({ data }) => setBanks(data || []));
  }, []);

  const loadCustomer = async (c) => {
    setSelCustomer(c);
    setDraft({ ...blankCustomerPayment(), customer_id: c.id, customer_name: c.name });
    setSelInvIds([]);
    setManualEntry({ paid_amount: "", deduction_amount: "", discount_input: "", discount_mode: "value" });
    // Fetch this customer's invoices (forgiving name match) and any past receipts
    const [{ data: invs }, { data: pays }] = await Promise.all([
      api.get("/sales/invoices"),
      api.get(`/customer-payments?customer_id=${c.id}`),
    ]);
    const norm = (s) => String(s || "").toUpperCase().replace(/&/g, "AND").replace(/[^A-Z0-9]/g, "");
    const target = norm(c.name);
    setInvoices((invs || []).filter((i) => norm(i.customer).includes(target) || target.includes(norm(i.customer))));
    setPayments(pays || []);
  };

  // Sum of invoice values for selected invoices — used to auto-fill the Amount field
  const selectedInvoicesTotal = useMemo(
    () => invoices
      .filter((i) => selInvIds.includes(i.id))
      .reduce((sum, i) => sum + Number(i.invoice_value || i.grand_total || 0), 0),
    [invoices, selInvIds]
  );

  // % → ₹ conversion. Discount basis = sum of selected invoices (or 0 if none).
  const computedDiscount = useMemo(() => {
    const raw = Number(manualEntry.discount_input || 0);
    if (!raw) return 0;
    if (manualEntry.discount_mode === "pct") {
      return Math.round((selectedInvoicesTotal * raw) / 100 * 100) / 100;
    }
    return raw;
  }, [manualEntry.discount_input, manualEntry.discount_mode, selectedInvoicesTotal]);

  // Whenever the manual Paid amount changes, push it into the receipt Amount field.
  // (User can still override.)
  useEffect(() => {
    const paid = Number(manualEntry.paid_amount || 0);
    if (paid > 0) {
      setDraft((d) => ({ ...d, amount: String(paid) }));
    }
  }, [manualEntry.paid_amount]);

  const totalInvoiced = useMemo(
    () => invoices.reduce((sum, i) => sum + Number(i.invoice_value || i.grand_total || 0), 0),
    [invoices]
  );
  const totalReceived = useMemo(
    () => payments.reduce((sum, p) => sum + Number(p.amount || 0), 0),
    [payments]
  );
  const outstanding = totalInvoiced - totalReceived;

  // Cumulative totals across all past receipts for this customer.
  const cumPaid = useMemo(
    () => payments.reduce((s, p) => s + Number(p.amount || 0), 0),
    [payments]
  );
  const cumDeduction = useMemo(
    () => payments.reduce((s, p) => s + Number(p.deduction_amount || 0), 0),
    [payments]
  );
  const cumDiscount = useMemo(
    () => payments.reduce((s, p) => s + Number(p.discount_amount || 0), 0),
    [payments]
  );

  const save = async () => {
    if (!selCustomer) return toast.error("Pick a customer first");
    if (!Number(draft.amount)) return toast.error("Amount must be > 0");
    setSaving(true);
    try {
      const { data } = await api.post("/customer-payments", {
        customer_id: selCustomer.id,
        customer_name: selCustomer.name,
        payment_date: draft.payment_date || todayISO(),
        amount: Number(draft.amount),
        deduction_amount: Number(manualEntry.deduction_amount || 0),
        discount_amount: Number(computedDiscount || 0),
        discount_pct: manualEntry.discount_mode === "pct" ? Number(manualEntry.discount_input || 0) : 0,
        check_number: draft.check_number || "",
        from_bank: draft.from_bank || "",
        to_bank_id: draft.to_bank_id || "",
        to_bank_name: draft.to_bank_name || "",
        to_bank_account_number: draft.to_bank_account_number || "",
        invoice_ids: selInvIds,
        remark: draft.remark || "",
      });
      toast.success(`Receipt ${data.receipt_no} recorded`);
      setDraft({ ...blankCustomerPayment(), customer_id: selCustomer.id, customer_name: selCustomer.name });
      setSelInvIds([]);
      setManualEntry({ paid_amount: "", deduction_amount: "", discount_input: "", discount_mode: "value" });
      const { data: pays } = await api.get(`/customer-payments?customer_id=${selCustomer.id}`);
      setPayments(pays || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Delete receipt ${p.receipt_no} (₹${fmtINR(p.amount)})?`)) return;
    try {
      await api.delete(`/customer-payments/${p.id}`);
      toast.success(`${p.receipt_no} deleted`);
      const { data: pays } = await api.get(`/customer-payments?customer_id=${selCustomer.id}`);
      setPayments(pays || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Pick Customer</label>
        <CustomerPicker customers={customers} value={selCustomer?.id} onSelect={loadCustomer} />
      </div>

      {selCustomer && (
        <>
          {/* ===== Running balance panel — 4 KPI cards (Opening / Sales / Paid / Closing) ===== */}
          <RunningBalancePanel kind="customer" party={{ id: selCustomer.id, name: selCustomer.name }} />

          {/* ===== Manual entry panel — drives the Amount field on the form ===== */}
          <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-4" data-testid="cp-manual-panel">
            <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-2">Manual Entry — drives the Amount field below</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Lbl label="Paid Amount (₹)">
                <input type="number" step="any" value={manualEntry.paid_amount}
                  onChange={(e) => setManualEntry({ ...manualEntry, paid_amount: e.target.value })}
                  placeholder="0.00" className={monoCls} data-testid="cp-manual-paid" />
              </Lbl>
              <Lbl label="Deduction Amount (₹)">
                <input type="number" step="any" value={manualEntry.deduction_amount}
                  onChange={(e) => setManualEntry({ ...manualEntry, deduction_amount: e.target.value })}
                  placeholder="0.00" className={monoCls} data-testid="cp-manual-deduction" />
              </Lbl>
              <Lbl label={`Discount Input (${manualEntry.discount_mode === "pct" ? "%" : "₹"})`}>
                <input type="number" step="any" value={manualEntry.discount_input}
                  onChange={(e) => setManualEntry({ ...manualEntry, discount_input: e.target.value })}
                  placeholder={manualEntry.discount_mode === "pct" ? "e.g. 5" : "0.00"}
                  className={monoCls} data-testid="cp-manual-discount-input" />
              </Lbl>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-slate-600 block mb-1">Discount Mode</label>
                <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-xs h-[38px]">
                  <button type="button"
                    onClick={() => setManualEntry({ ...manualEntry, discount_mode: "value" })}
                    data-testid="cp-discount-mode-value"
                    className={`flex-1 font-semibold ${manualEntry.discount_mode === "value" ? "bg-[#1D2951] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                    ₹ Value
                  </button>
                  <button type="button"
                    onClick={() => setManualEntry({ ...manualEntry, discount_mode: "pct" })}
                    data-testid="cp-discount-mode-pct"
                    className={`flex-1 font-semibold border-l border-slate-300 ${manualEntry.discount_mode === "pct" ? "bg-[#1D2951] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                    % of selected
                  </button>
                </div>
                {manualEntry.discount_mode === "pct" && Number(manualEntry.discount_input || 0) > 0 && (
                  <div className="text-[10px] text-amber-800 mt-1" data-testid="cp-discount-computed">
                    = ₹ {fmtINR(computedDiscount)} (of selected ₹ {fmtINR(selectedInvoicesTotal)})
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== Record Payment form (NOW ABOVE the sales invoice table) ===== */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-slate-900 mb-3">Record Customer Payment</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Lbl label="Customer Name">
                <input value={draft.customer_name} readOnly
                  className={`${inputCls} bg-slate-50`} data-testid="cp-customer-name" />
              </Lbl>
              <Lbl label="Cheque / Ref Number">
                <input value={draft.check_number}
                  onChange={(e) => setDraft({ ...draft, check_number: e.target.value })}
                  placeholder="000123 / NEFT-..." className={`${inputCls} font-mono`} data-testid="cp-check-number" />
              </Lbl>
              <Lbl label="Payment Date">
                <input type="date" value={draft.payment_date}
                  onChange={(e) => setDraft({ ...draft, payment_date: e.target.value })}
                  className={inputCls} data-testid="cp-payment-date" />
              </Lbl>
              <Lbl label="Amount ₹ * (auto from Paid Amount above)">
                <input type="number" step="any" value={draft.amount}
                  onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                  placeholder="0.00" className={monoCls} data-testid="cp-amount" />
              </Lbl>
              <Lbl label="From Bank (Customer's)">
                <input value={draft.from_bank}
                  onChange={(e) => setDraft({ ...draft, from_bank: e.target.value })}
                  placeholder="HDFC Bank, etc." className={inputCls} data-testid="cp-from-bank" />
              </Lbl>
              <Lbl label="To Bank (Ours)">
                <BankPicker banks={banks} value={draft.to_bank_id}
                  onSelect={(b) => setDraft({ ...draft, to_bank_id: b.id, to_bank_name: b.bank_name, to_bank_account_number: b.account_number })} />
              </Lbl>
              <Lbl label="Remark" className="md:col-span-3">
                <input value={draft.remark}
                  onChange={(e) => setDraft({ ...draft, remark: e.target.value })}
                  placeholder="Optional note" className={inputCls} data-testid="cp-remark" />
              </Lbl>
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={save} disabled={saving} data-testid="cp-save"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md inline-flex items-center gap-1.5">
                <Save size={14} /> {saving ? "Saving…" : "Record Payment"}
              </button>
            </div>
          </div>

          {/* ===== Invoice summary ===== */}
          <div className="grid grid-cols-3 gap-3">
            <SummaryCard label="Total Invoiced" value={`₹ ${fmtINR(totalInvoiced)}`} testid="cp-summary-invoiced" />
            <SummaryCard label="Total Received" value={`₹ ${fmtINR(totalReceived)}`} testid="cp-summary-received" />
            <SummaryCard label="Outstanding" value={`₹ ${fmtINR(outstanding)}`} accent={outstanding > 0 ? "amber" : "emerald"} testid="cp-summary-outstanding" />
          </div>

          {/* ===== Invoice list with selection ===== */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex justify-between items-center gap-3">
              <span>Sales Invoices for {selCustomer.name} <span className="text-slate-400 font-normal">({invoices.length})</span></span>
              {selInvIds.length > 0 && (
                <span className="text-emerald-700" data-testid="cp-inv-selected">
                  {selInvIds.length} selected · ₹ {fmtINR(selectedInvoicesTotal)}
                </span>
              )}
            </div>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      data-testid="cp-inv-check-all"
                      checked={invoices.length > 0 && selInvIds.length === invoices.length}
                      onChange={(e) => setSelInvIds(e.target.checked ? invoices.map((i) => i.id) : [])} />
                  </th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Invoice #</th>
                  <th className="px-3 py-2 text-left">PO #</th>
                  <th className="px-3 py-2 text-left">Part</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Invoice Value</th>
                </tr>
              </thead>
              <tbody data-testid="cp-invoice-table">
                {invoices.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-6 text-slate-400 italic">No invoices for this customer</td></tr>
                ) : invoices.map((i) => {
                  const isChecked = selInvIds.includes(i.id);
                  return (
                    <tr key={i.id} className={`border-t border-slate-100 hover:bg-slate-50 ${isChecked ? "bg-emerald-50/40" : ""}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) setSelInvIds([...selInvIds, i.id]);
                            else setSelInvIds(selInvIds.filter((x) => x !== i.id));
                          }}
                          data-testid={`cp-inv-check-${i.invoice_no}`} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{i.invoice_date}</td>
                      <td className="px-3 py-2 font-mono font-semibold">{i.invoice_no}</td>
                      <td className="px-3 py-2 font-mono text-xs">{i.po_no}</td>
                      <td className="px-3 py-2 text-xs">{i.part_no} {i.part_name ? `· ${i.part_name}` : ""}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtINR(i.dispatch_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(i.invoice_value || i.grand_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ===== Past payments log — now with cumulative Paid/Deduction/Discount columns ===== */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex justify-between items-center gap-3 flex-wrap">
              <span>Past Receipts <span className="text-slate-400 font-normal">({payments.length})</span></span>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] text-slate-500 font-normal" data-testid="cp-cumulative-strip">
                  Cumulative · Paid ₹ {fmtINR(cumPaid)} · Deduction ₹ {fmtINR(cumDeduction)} · Discount ₹ {fmtINR(cumDiscount)}
                </span>
                <TableExportButton
                  testIdPrefix="cp-export"
                  filename={`customer_payments_${(selCustomer?.name || "all").replace(/\s+/g, "_")}`}
                  rows={payments}
                  dateField="payment_date"
                  columns={[
                    { key: "receipt_no", label: "Receipt No" },
                    { key: "payment_date", label: "Date" },
                    { key: "customer_name", label: "Customer" },
                    { key: "amount", label: "Paid Amount" },
                    { key: "deduction_amount", label: "Deduction" },
                    { key: "discount_amount", label: "Discount" },
                    { key: "discount_pct", label: "Discount %" },
                    { key: "check_number", label: "Cheque / Ref" },
                    { key: "from_bank", label: "From Bank" },
                    { key: "to_bank_name", label: "To Bank" },
                    { key: "remark", label: "Remark" },
                  ]}
                />
              </div>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Receipt #</th>
                  <th className="px-3 py-2 text-left">Cheque #</th>
                  <th className="px-3 py-2 text-left">From Bank</th>
                  <th className="px-3 py-2 text-left">To Bank</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Deduction</th>
                  <th className="px-3 py-2 text-right">Discount</th>
                  <th className="px-3 py-2 text-right text-emerald-700">Cum. Paid</th>
                  <th className="px-3 py-2 text-right text-amber-700">Cum. Deduction</th>
                  <th className="px-3 py-2 text-right text-violet-700">Cum. Discount</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody data-testid="cp-payments-table">
                {payments.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-6 text-slate-400 italic">No receipts recorded yet</td></tr>
                ) : (() => {
                  // Build oldest → newest order for running totals; render newest first.
                  const ordered = [...payments].sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
                  let rp = 0, rd = 0, rdc = 0;
                  const enriched = ordered.map((p) => {
                    rp += Number(p.amount || 0);
                    rd += Number(p.deduction_amount || 0);
                    rdc += Number(p.discount_amount || 0);
                    return { ...p, _cum_paid: rp, _cum_ded: rd, _cum_disc: rdc };
                  });
                  enriched.reverse();
                  return enriched.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs">{p.payment_date}</td>
                      <td className="px-3 py-2 font-mono font-semibold">{p.receipt_no}</td>
                      <td className="px-3 py-2 font-mono text-xs">{p.check_number || "—"}</td>
                      <td className="px-3 py-2 text-xs">{p.from_bank || "—"}</td>
                      <td className="px-3 py-2 text-xs">{p.to_bank_name || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">₹ {fmtINR(p.amount)}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700">{Number(p.deduction_amount || 0) ? `₹ ${fmtINR(p.deduction_amount)}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-violet-700">
                        {Number(p.discount_amount || 0) ? (
                          <span>
                            ₹ {fmtINR(p.discount_amount)}
                            {Number(p.discount_pct || 0) > 0 && (
                              <span className="text-[10px] text-slate-400"> ({p.discount_pct}%)</span>
                            )}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700" data-testid={`cp-cum-paid-${p.receipt_no}`}>₹ {fmtINR(p._cum_paid)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-amber-700" data-testid={`cp-cum-ded-${p.receipt_no}`}>₹ {fmtINR(p._cum_ded)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-violet-700" data-testid={`cp-cum-disc-${p.receipt_no}`}>₹ {fmtINR(p._cum_disc)}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => deletePayment(p)} data-testid={`cp-delete-${p.receipt_no}`}
                          className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const blankCustomerPayment = () => ({
  customer_id: "", customer_name: "",
  payment_date: todayISO(),
  amount: "",
  check_number: "",
  from_bank: "",
  to_bank_id: "", to_bank_name: "", to_bank_account_number: "",
  remark: "",
});

const SummaryCard = ({ label, value, accent, testid }) => {
  const tone = accent === "amber"
    ? "bg-amber-50 border-amber-200 text-amber-800"
    : accent === "emerald"
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold font-mono">{value}</div>
    </div>
  );
};

function CustomerPicker({ customers, value, onSelect }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = customers.filter((c) =>
    !q || `${c.name} ${c.gst || ""} ${c.contact || ""}`.toLowerCase().includes(q.toLowerCase())
  );
  const selected = customers.find((c) => c.id === value);
  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        value={open ? q : (selected?.name || "")}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setQ(""); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search customer…"
        data-testid="cp-customer-picker"
        className={`${inputCls} pl-9`}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">No customers — add in Customer Master</div>
          ) : filtered.map((c) => (
            <button key={c.id} type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
              data-testid={`cp-customer-opt-${c.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
              <div className="font-medium">{c.name}</div>
              <div className="text-[10px] text-slate-500 font-mono">{c.gst || ""}{c.contact ? ` · ${c.contact}` : ""}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// =========================================================================
// BANK RECO FLOW — month-scoped bank ledger per company + account
// =========================================================================
function BankRecoFlow() {
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selCompanyId, setSelCompanyId] = useState("");
  const [selBankId, setSelBankId] = useState("");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Opening-balance prompt (only when the picked bank has no opening_balance yet)
  const [showOpenPrompt, setShowOpenPrompt] = useState(false);
  const [openVal, setOpenVal] = useState("");

  // Manual entry form
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ date: todayISO(), particulars: "", paid_amount: "", received_amount: "", remark: "" });

  useEffect(() => {
    api.get("/companies-book").then(({ data }) => setCompanies(data || []));
    api.get("/banks").then(({ data }) => setBanks(data || []));
  }, []);

  // When company changes, filter banks; auto-pick the first matching bank if any
  const matchingBanks = useMemo(() => {
    if (!selCompanyId) return banks;
    return banks.filter((b) => b.linked_company_id === selCompanyId);
  }, [banks, selCompanyId]);

  useEffect(() => {
    // If the current selBankId no longer matches the company, reset
    if (selBankId && !matchingBanks.some((b) => b.id === selBankId)) {
      setSelBankId("");
    }
  }, [matchingBanks, selBankId]);

  const reload = async () => {
    if (!selBankId) { setData(null); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get(`/bank-reco/${selBankId}?year=${year}&month=${month}`);
      setData(d);
      // If opening balance is 0 AND no prior tx exist, prompt the user to seed it
      const bank = banks.find((b) => b.id === selBankId);
      if (bank && !bank.opening_balance && d.opening_balance === 0 && (d.rows || []).length === 0) {
        setShowOpenPrompt(true);
      } else {
        setShowOpenPrompt(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bank reco");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [selBankId, year, month]);

  const saveOpening = async () => {
    const v = Number(openVal);
    if (!Number.isFinite(v)) return toast.error("Enter a valid opening balance");
    const bank = banks.find((b) => b.id === selBankId);
    if (!bank) return;
    try {
      await api.put(`/banks/${selBankId}`, {
        ...bank,
        opening_balance: v,
        opening_balance_date: `${year}-${String(month).padStart(2, "0")}-01`,
      });
      toast.success("Opening balance saved");
      setShowOpenPrompt(false);
      setOpenVal("");
      // refresh banks list to pick up the new opening_balance
      const { data: bs } = await api.get("/banks");
      setBanks(bs || []);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveManual = async () => {
    if (!manual.particulars.trim()) return toast.error("Particulars required");
    const p = Number(manual.paid_amount || 0);
    const r = Number(manual.received_amount || 0);
    if (p <= 0 && r <= 0) return toast.error("Either paid or received must be > 0");
    try {
      await api.post(`/bank-reco/${selBankId}/transaction`, {
        date: manual.date,
        particulars: manual.particulars.trim(),
        paid_amount: p,
        received_amount: r,
        remark: manual.remark,
      });
      toast.success("Entry added");
      setManual({ date: todayISO(), particulars: "", paid_amount: "", received_amount: "", remark: "" });
      setShowManual(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteManual = async (txId) => {
    if (!window.confirm("Delete this manual entry?")) return;
    try {
      await api.delete(`/bank-reco/transaction/${txId}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Year selector — last 6 years + current
  const yearOpts = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 6; y--) yearOpts.push(y);
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="space-y-4">
      {/* ===== Filter strip: Company + Bank + Year + Month ===== */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Lbl label="Company *">
          <select value={selCompanyId} onChange={(e) => setSelCompanyId(e.target.value)}
            data-testid="br-company" className={inputCls}>
            <option value="">— pick company —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Lbl>
        <Lbl label="Bank Account *">
          <select value={selBankId} onChange={(e) => setSelBankId(e.target.value)}
            data-testid="br-bank" className={inputCls} disabled={!selCompanyId && banks.length === 0}>
            <option value="">— pick account —</option>
            {matchingBanks.map((b) => (
              <option key={b.id} value={b.id}>{b.bank_name} · {b.account_number}</option>
            ))}
          </select>
          {selCompanyId && matchingBanks.length === 0 && (
            <div className="text-[10px] text-amber-700 mt-1">No bank linked to this company yet — add one in Bank Master.</div>
          )}
        </Lbl>
        <Lbl label="Year">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            data-testid="br-year" className={inputCls}>
            {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </Lbl>
        <Lbl label="Month">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="br-month" className={inputCls}>
            {monthNames.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </Lbl>
      </div>

      {/* ===== Opening balance prompt (first-time setup) ===== */}
      {showOpenPrompt && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 flex-wrap">
          <div className="text-sm flex-1">
            <div className="font-semibold text-amber-900">First-time setup — enter the opening balance for this account.</div>
            <div className="text-xs text-amber-700">This is recorded once. From next month onwards, the closing balance auto-carries over.</div>
          </div>
          <input type="number" step="any" value={openVal} onChange={(e) => setOpenVal(e.target.value)}
            placeholder="₹ opening balance" data-testid="br-opening-input"
            className={`${inputCls} max-w-xs font-mono`} />
          <button onClick={saveOpening} data-testid="br-opening-save"
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md">Save</button>
        </div>
      )}

      {/* ===== Ledger table ===== */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Opening Balance" value={`₹ ${fmtINR(data.opening_balance)}`} testid="br-summary-open" />
            <SummaryCard label="Total Received" value={`₹ ${fmtINR(data.totals.received)}`} accent="emerald" testid="br-summary-received" />
            <SummaryCard label="Total Paid" value={`₹ ${fmtINR(data.totals.paid)}`} accent="amber" testid="br-summary-paid" />
            <SummaryCard label="Closing Balance" value={`₹ ${fmtINR(data.closing_balance)}`} accent={data.closing_balance >= 0 ? "emerald" : "amber"} testid="br-summary-close" />
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex justify-between items-center">
              <span>{data.bank.bank_name} · {data.bank.account_number} <span className="text-slate-400 font-normal">({data.month_label})</span></span>
              <button onClick={() => setShowManual(true)} data-testid="br-add-manual"
                className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1">
                <Plus size={12} /> Manual Entry
              </button>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Particulars</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Received</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  <th className="px-3 py-2 text-center w-12"></th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-slate-100/70">
                  <td colSpan={5} className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Opening</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800">₹ {fmtINR(data.opening_balance)}</td>
                  <td></td>
                </tr>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">Loading…</td></tr>
                ) : data.rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">No transactions for this month</td></tr>
                ) : data.rows.map((r, idx) => (
                  <tr key={`${r.source}-${r.tx_id || r.reference || idx}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
                    <td className="px-3 py-2 text-xs">{r.particulars}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.reference || (r.source === "manual" ? "(manual)" : "—")}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-amber-700">{r.paid_amount ? `₹ ${fmtINR(r.paid_amount)}` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">{r.received_amount ? `₹ ${fmtINR(r.received_amount)}` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">₹ {fmtINR(r.balance)}</td>
                    <td className="px-3 py-2 text-center">
                      {r.source === "manual" && (
                        <button onClick={() => deleteManual(r.tx_id)} className="p-1 text-rose-600 hover:bg-rose-50 rounded" title="Delete manual entry">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-emerald-50/50 border-t-2 border-emerald-200">
                  <td colSpan={5} className="px-3 py-2.5 text-right text-xs font-semibold text-emerald-800 uppercase tracking-wider">Closing</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-700">₹ {fmtINR(data.closing_balance)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Manual entry modal */}
      {showManual && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowManual(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold">Manual Bank Entry</h3>
              <button onClick={() => setShowManual(false)} className="text-slate-400 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="Date">
                <input type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })}
                  className={inputCls + " font-mono"} data-testid="br-manual-date" />
              </Lbl>
              <Lbl label="Particulars *" className="col-span-2">
                <input value={manual.particulars} onChange={(e) => setManual({ ...manual, particulars: e.target.value })}
                  placeholder="e.g. Bank charges, Interest, Direct deposit"
                  className={inputCls} data-testid="br-manual-particulars" />
              </Lbl>
              <Lbl label="Paid (outflow)">
                <input type="number" step="any" value={manual.paid_amount}
                  onChange={(e) => setManual({ ...manual, paid_amount: e.target.value })}
                  className={inputCls + " font-mono"} data-testid="br-manual-paid" />
              </Lbl>
              <Lbl label="Received (inflow)">
                <input type="number" step="any" value={manual.received_amount}
                  onChange={(e) => setManual({ ...manual, received_amount: e.target.value })}
                  className={inputCls + " font-mono"} data-testid="br-manual-received" />
              </Lbl>
              <Lbl label="Remark" className="col-span-2">
                <input value={manual.remark} onChange={(e) => setManual({ ...manual, remark: e.target.value })}
                  className={inputCls} data-testid="br-manual-remark" />
              </Lbl>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowManual(false)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button onClick={saveManual} data-testid="br-manual-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-3 py-1.5 rounded-md">Save Entry</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
